import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@adop/ui';
import { Check } from 'lucide-react';
import { DEFAULT_ACCENT_COLOR, DEFAULT_PRIMARY_COLOR, FONTS } from '@adop/shared';
import type { RegisterResponse } from '@adop/shared';
import { postJson, errorMessage, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Alert } from '../components/Alert';
import { SlugInput } from '../components/SlugInput';
import { StorePreview } from '../components/StorePreview';
import { DevAuthForm } from '../components/DevAuthForm';

interface FormState {
  restaurantName: string;
  restaurantEmail: string;
  locationName: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  timezone: string;
  slug: string;
  primaryColor: string;
  accentColor: string;
  font: string;
}

function defaultForm(): FormState {
  let timezone = 'America/New_York';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || timezone;
  } catch {
    // keep default
  }
  return {
    restaurantName: '',
    restaurantEmail: '',
    locationName: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    phone: '',
    timezone,
    slug: '',
    primaryColor: DEFAULT_PRIMARY_COLOR,
    accentColor: DEFAULT_ACCENT_COLOR,
    font: 'inter',
  };
}

const STEPS = ['Restaurant', 'Location', 'Web address'];

export function RegisterPage() {
  const { loading, user, me, refresh, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && me?.profile) navigate('/dashboard', { replace: true });
  }, [loading, me, navigate]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const canContinue =
    step === 0
      ? form.restaurantName.trim().length > 0
      : step === 1
        ? form.locationName.trim().length > 0 && form.timezone.trim().length > 0
        : form.slug.trim().length >= 3;

  const goNext = () => {
    // Most restaurants only have one location: default its name to the
    // restaurant name so onboarding only asks for what matters.
    if (step === 0 && form.locationName.trim().length === 0) {
      update('locationName', form.restaurantName.trim());
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson<RegisterResponse>('/api/platform/register', {
        restaurant_name: form.restaurantName.trim(),
        ...(form.restaurantEmail.trim() ? { restaurant_email: form.restaurantEmail.trim() } : {}),
        location: {
          name: form.locationName.trim(),
          slug: form.slug.trim().toLowerCase(),
          address: {
            line1: form.line1.trim(),
            line2: form.line2.trim(),
            city: form.city.trim(),
            state: form.state.trim(),
            postal_code: form.postalCode.trim(),
            country: 'US',
          },
          ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
          timezone: form.timezone.trim(),
          branding: {
            primary_color: form.primaryColor,
            accent_color: form.accentColor,
            font: form.font,
          },
        },
      });
      // Refetch the account before entering the dashboard: without this the
      // workspace has no membership/location and every page looks empty until a
      // manual refresh.
      await refresh();
      navigate('/dashboard', { replace: true });
    } catch (cause) {
      const alreadyRegistered =
        cause instanceof ApiError &&
        cause.status === 409 &&
        cause.message.toLowerCase().includes('already has a restaurant');
      if (alreadyRegistered) {
        // A double submit or retry: the account exists, so just continue.
        await refresh();
        navigate('/dashboard', { replace: true });
        return;
      }
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-4">
        <Card className="w-full max-w-md">
          <div className="space-y-4 text-center">
            <h1 className="text-xl font-semibold text-zinc-900">Register your restaurant</h1>
            <p className="text-sm text-zinc-500">Sign in first so we can connect your account.</p>
            <Button fullWidth onClick={() => signInWithGoogle()}>
              Continue with Google
            </Button>
            <DevAuthForm onSignedIn={() => undefined} />
            <Link to="/" className="block text-sm text-zinc-500 hover:text-zinc-900">
              ← Back to marketplace
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900">
          ← adop
        </Link>
        <h1 className="mt-3 text-2xl font-bold tracking-tight text-zinc-900">Set up your restaurant</h1>
        <p className="mt-1 text-sm text-zinc-500">Three quick steps and you&apos;re on the dashboard.</p>

        <div className="mt-6 flex items-center gap-2">
          {STEPS.map((label, index) => (
            <div key={label} className="flex flex-1 items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  index < step
                    ? 'bg-emerald-500 text-white'
                    : index === step
                      ? 'bg-zinc-900 text-white'
                      : 'bg-zinc-200 text-zinc-500'
                }`}
              >
                {index < step ? <Check className="h-4 w-4" /> : index + 1}
              </span>
              <span className={`text-sm ${index === step ? 'font-medium text-zinc-900' : 'text-zinc-500'}`}>
                {label}
              </span>
              {index < STEPS.length - 1 ? <span className="h-px flex-1 bg-zinc-200" /> : null}
            </div>
          ))}
        </div>

        <Card className="mt-6">
          {step === 0 ? (
            <div className="space-y-4">
              <Input
                label="Restaurant name"
                value={form.restaurantName}
                onChange={(event) => update('restaurantName', event.target.value)}
                placeholder="Joe's Pizza"
                required
              />
              <Input
                label="Contact email"
                type="email"
                hint="Where we send account and billing notices. Optional."
                value={form.restaurantEmail}
                onChange={(event) => update('restaurantEmail', event.target.value)}
                placeholder="owner@example.com"
              />
            </div>
          ) : step === 1 ? (
            <div className="space-y-4">
              <Input
                label="Location name"
                hint="Most restaurants have one location — you can add more later from Locations."
                value={form.locationName}
                onChange={(event) => update('locationName', event.target.value)}
                placeholder="Downtown"
                required
              />
              <Input
                label="Address line 1"
                value={form.line1}
                onChange={(event) => update('line1', event.target.value)}
              />
              <Input
                label="Address line 2"
                value={form.line2}
                onChange={(event) => update('line2', event.target.value)}
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label="City"
                  value={form.city}
                  onChange={(event) => update('city', event.target.value)}
                />
                <Input
                  label="State"
                  value={form.state}
                  onChange={(event) => update('state', event.target.value)}
                />
                <Input
                  label="ZIP"
                  value={form.postalCode}
                  onChange={(event) => update('postalCode', event.target.value)}
                />
              </div>
              <Input
                label="Phone"
                value={form.phone}
                onChange={(event) => update('phone', event.target.value)}
                placeholder="(555) 123-4567"
              />
              <Input
                label="Timezone"
                hint="Auto-detected from your browser. Used for hours and receipts."
                value={form.timezone}
                onChange={(event) => update('timezone', event.target.value)}
                required
              />
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <p className="mb-2 text-sm font-medium text-zinc-900">Choose your web address</p>
                <SlugInput value={form.slug} onChange={(value) => update('slug', value)} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-zinc-900" htmlFor="primary-color">
                    Primary colour
                  </label>
                  <input
                    id="primary-color"
                    type="color"
                    value={form.primaryColor}
                    onChange={(event) => update('primaryColor', event.target.value)}
                    className="h-10 w-full cursor-pointer rounded-lg border border-zinc-200"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-zinc-900" htmlFor="accent-color">
                    Accent colour
                  </label>
                  <input
                    id="accent-color"
                    type="color"
                    value={form.accentColor}
                    onChange={(event) => update('accentColor', event.target.value)}
                    className="h-10 w-full cursor-pointer rounded-lg border border-zinc-200"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-zinc-900" htmlFor="font">
                  Font
                </label>
                <select
                  id="font"
                  value={form.font}
                  onChange={(event) => update('font', event.target.value)}
                  className="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm"
                >
                  {FONTS.map((font) => (
                    <option key={font.id} value={font.id}>
                      {font.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-zinc-500">
                You can upload a logo from the Branding page once your restaurant is created.
              </p>
              <StorePreview
                name={form.restaurantName}
                about={null}
                branding={{
                  primary_color: form.primaryColor,
                  accent_color: form.accentColor,
                  font: form.font as (typeof FONTS)[number]['id'],
                }}
              />
            </div>
          )}

          {error ? <div className="mt-4"><Alert>{error}</Alert></div> : null}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
              Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button disabled={!canContinue} onClick={goNext}>
                Continue
              </Button>
            ) : (
              <Button loading={busy} disabled={!canContinue} onClick={submit}>
                Create restaurant
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
